import { type ComponentType, type ReactElement, type ReactNode, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import type { Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Building2, Eye, EyeOff, FileText, KeyRound, MapPin, Plus, ShieldCheck, Trash2, UserRound } from 'lucide-react'
import type { Company, CompanyCredentials, CompanySettings } from '@/types/company.types'
import { settingsReplacementPatch } from '@/features/companies/utils/settingsPatch'
import { Button, Card, Input, Select } from '@/components/ui'
import { PermissionGate } from '@/components/common/PermissionGate'
import { usePermissions } from '@/hooks/usePermissions'
import { useIsOrgAdmin } from '@/hooks/useIsOrgAdmin'
import { NATIONALITY_OPTIONS } from '@/lib/nationalityOptions'
import { useUpdateCompany } from '../hooks/useCompanies'
import {
  companySettingsFormSchema,
  type CompanySettingsFormValues,
} from '../schemas/company.schemas'

interface CompanySettingsTabProps {
  company: Company
}

interface SettingsSectionProps {
  title: string
  description: string
  icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>
  children: ReactNode
}

interface InfoItemProps {
  label: string
  value?: string
}

function stringValue(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function displayValue(value: string | undefined): string {
  const trimmed = value?.trim()
  return trimmed ? trimmed : 'Not provided'
}

function countryLabel(value: string | undefined): string {
  const trimmed = value?.trim()
  if (!trimmed) return 'Not provided'
  return NATIONALITY_OPTIONS.find((option) => option.value === trimmed)?.label ?? trimmed
}

function formValuesFromSettings(settings: CompanySettings | undefined): CompanySettingsFormValues {
  const address = settings?.address
  const credentials = settings?.credentials
  return {
    contactName: stringValue(settings?.contactName),
    contactEmail: stringValue(settings?.contactEmail),
    contactPhone: stringValue(settings?.contactPhone),
    street: stringValue(address?.street),
    city: stringValue(address?.city),
    state: stringValue(address?.state),
    country: stringValue(address?.country),
    postalCode: stringValue(address?.postalCode),
    timezone: stringValue(settings?.timezone),
    clientInstructions: stringValue(settings?.clientInstructions),
    credentialConsoleLink: stringValue(credentials?.consoleLink),
    credentialPortalUrl: stringValue(credentials?.portalUrl),
    credentialCompanyId: stringValue(credentials?.companyId),
    credentialUsername: stringValue(credentials?.username),
    credentialPassword: stringValue(credentials?.password),
    credentialPhoneUsername: stringValue(credentials?.phoneCredentials?.username),
    credentialPhonePassword: stringValue(credentials?.phoneCredentials?.password),
    credentialPhoneExtensionNumber: stringValue(credentials?.phoneCredentials?.extensionNumber),
    credentialDispenseDetails: stringValue(credentials?.dispenseSystem?.details),
    credentialDispenseExtensionNumbers: stringValue(credentials?.dispenseSystem?.extensionNumbers),
    credentialDispenseOperators: credentials?.dispenseSystem?.operators?.map((operator) => ({
      name: stringValue(operator.name),
      username: stringValue(operator.username),
      password: stringValue(operator.password),
      extensionNumber: stringValue(operator.extensionNumber),
    })) ?? [],
    credentialCustomFields: credentials?.customFields?.map((field) => ({
      label: stringValue(field.label),
      value: stringValue(field.value),
      type: stringValue(field.type),
    })) ?? [],
    credentialGateCode: stringValue(credentials?.gateCode),
    credentialAlarmCode: stringValue(credentials?.alarmCode),
    credentialWifiName: stringValue(credentials?.wifiName),
    credentialWifiPassword: stringValue(credentials?.wifiPassword),
    credentialNotes: stringValue(credentials?.notes),
  }
}

function cleanText(value: string | undefined): string | undefined {
  const trimmed = value?.trim()
  return trimmed ? trimmed : undefined
}

const removedSettingsKeys = new Set([
  'geo',
  'geofenceRadiusMeters',
  'internalNotes',
  'defaultShiftStart',
  'defaultShiftEnd',
  'requiredHeadcount',
])

function credentialsFromForm(values: CompanySettingsFormValues): CompanyCredentials | undefined {
  const phoneCredentials = {
    username: cleanText(values.credentialPhoneUsername),
    password: cleanText(values.credentialPhonePassword),
    extensionNumber: cleanText(values.credentialPhoneExtensionNumber),
  }
  const operators = (values.credentialDispenseOperators ?? [])
    .map((operator) => ({
      name: cleanText(operator.name),
      username: cleanText(operator.username),
      password: cleanText(operator.password),
      extensionNumber: cleanText(operator.extensionNumber),
    }))
    .filter((operator) => Object.values(operator).some((v) => v !== undefined))
  const dispenseSystem = {
    details: cleanText(values.credentialDispenseDetails),
    extensionNumbers: cleanText(values.credentialDispenseExtensionNumbers),
    operators: operators.length > 0 ? operators : undefined,
  }
  const customFields = (values.credentialCustomFields ?? [])
    .map((field) => ({
      label: cleanText(field.label) ?? '',
      value: cleanText(field.value),
      type: cleanText(field.type),
    }))
    .filter((field) => field.label)
  const credentials: CompanyCredentials = {
    consoleLink: cleanText(values.credentialConsoleLink),
    portalUrl: cleanText(values.credentialPortalUrl),
    companyId: cleanText(values.credentialCompanyId),
    username: cleanText(values.credentialUsername),
    password: cleanText(values.credentialPassword),
    phoneCredentials: Object.values(phoneCredentials).some((v) => v !== undefined)
      ? phoneCredentials
      : undefined,
    dispenseSystem: Object.values(dispenseSystem).some((v) => v !== undefined)
      ? dispenseSystem
      : undefined,
    customFields: customFields.length > 0 ? customFields : undefined,
    gateCode: cleanText(values.credentialGateCode),
    alarmCode: cleanText(values.credentialAlarmCode),
    wifiName: cleanText(values.credentialWifiName),
    wifiPassword: cleanText(values.credentialWifiPassword),
    notes: cleanText(values.credentialNotes),
  }
  // Drop the block entirely when every field is empty so we don't persist an
  // empty object.
  const hasValue = Object.values(credentials).some((v) => v !== undefined)
  return hasValue ? credentials : undefined
}

function settingsFromForm(
  values: CompanySettingsFormValues,
  existing: CompanySettings | undefined,
  isOrgAdmin: boolean
): CompanySettings {
  // `credentials` is org-admin-only. When the editor is not an org admin, we
  // must never rebuild it from the (empty, non-rendered) form fields — that
  // would wipe stored credentials. Preserving it here belt-and-braces with the
  // `...rest` spread that already carries unknown keys through.
  const rest = Object.fromEntries(
    Object.entries(existing ?? {}).filter(
      ([key]) => !removedSettingsKeys.has(key) && key !== 'credentials'
    )
  )

  const next: CompanySettings = {
    ...rest,
    contactName: cleanText(values.contactName),
    contactEmail: cleanText(values.contactEmail),
    contactPhone: cleanText(values.contactPhone),
    address: {
      ...(existing?.address ?? {}),
      street: cleanText(values.street),
      city: cleanText(values.city),
      state: cleanText(values.state),
      country: cleanText(values.country),
      postalCode: cleanText(values.postalCode),
    },
    timezone: cleanText(values.timezone),
    clientInstructions: cleanText(values.clientInstructions),
  }

  const credentials = isOrgAdmin ? credentialsFromForm(values) : existing?.credentials
  if (credentials) next.credentials = credentials

  return next
}

function SettingsSection({
  title,
  description,
  icon: Icon,
  children,
}: SettingsSectionProps): ReactElement {
  return (
    <Card contentClassName="p-5 sm:p-6">
      <div className="grid gap-5 lg:grid-cols-[220px_minmax(0,1fr)]">
        <div className="space-y-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary dark:bg-primary/20">
            <Icon className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {title}
            </h3>
            <p className="mt-1 text-sm leading-6 text-gray-500 dark:text-gray-400">
              {description}
            </p>
          </div>
        </div>
        <div className="grid gap-4">{children}</div>
      </div>
    </Card>
  )
}

function InfoItem({ label, value }: InfoItemProps): ReactElement {
  return (
    <div className="space-y-1">
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
        {label}
      </p>
      <p className="min-h-5 text-sm font-medium text-gray-900 dark:text-gray-100">
        {displayValue(value)}
      </p>
    </div>
  )
}

function SecretInfoItem({ label, value }: InfoItemProps): ReactElement {
  const [revealed, setRevealed] = useState(false)
  const trimmed = value?.trim()

  return (
    <div className="space-y-1">
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
        {label}
      </p>
      {trimmed ? (
        <div className="flex items-center gap-2">
          <p className="min-h-5 text-sm font-medium text-gray-900 dark:text-gray-100">
            {revealed ? trimmed : '••••••••'}
          </p>
          <button
            type="button"
            className="rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/30 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-300"
            onClick={() => setRevealed((v) => !v)}
            aria-label={revealed ? `Hide ${label}` : `Reveal ${label}`}
          >
            {revealed ? (
              <EyeOff className="h-4 w-4" aria-hidden />
            ) : (
              <Eye className="h-4 w-4" aria-hidden />
            )}
          </button>
        </div>
      ) : (
        <p className="min-h-5 text-sm font-medium text-gray-900 dark:text-gray-100">
          Not provided
        </p>
      )}
    </div>
  )
}

function ReadOnlyInfoSection({
  title,
  description,
  icon: Icon,
  children,
}: SettingsSectionProps): ReactElement {
  return (
    <Card contentClassName="p-5 sm:p-6">
      <div className="space-y-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary dark:bg-primary/20">
            <Icon className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {title}
            </h3>
            <p className="mt-1 text-sm leading-6 text-gray-500 dark:text-gray-400">
              {description}
            </p>
          </div>
        </div>
        {children}
      </div>
    </Card>
  )
}

const textAreaClasses =
  'min-h-28 w-full resize-y rounded-xl border border-gray-200 bg-white/95 px-3.5 py-3 text-sm text-gray-900 shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition-all duration-150 placeholder:text-gray-400 focus:border-primary/40 focus:outline-none focus:ring-4 focus:ring-primary/10 dark:border-gray-700 dark:bg-gray-800/90 dark:text-gray-100 dark:placeholder:text-gray-500'

export function CompanySettingsTab({ company }: CompanySettingsTabProps): ReactElement {
  const { can } = usePermissions()
  const canUpdate = can('companies', 'update')
  const isOrgAdmin = useIsOrgAdmin()
  const updateCompany = useUpdateCompany()
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<CompanySettingsFormValues>({
    resolver: zodResolver(companySettingsFormSchema) as Resolver<CompanySettingsFormValues>,
    defaultValues: formValuesFromSettings(company.settings),
  })
  const {
    fields: operatorFields,
    append: appendOperator,
    remove: removeOperator,
  } = useFieldArray({
    control,
    name: 'credentialDispenseOperators',
  })
  const {
    fields: customFieldRows,
    append: appendCustomField,
    remove: removeCustomField,
  } = useFieldArray({
    control,
    name: 'credentialCustomFields',
  })

  const onSubmit = (values: CompanySettingsFormValues): void => {
    const nextSettings = settingsFromForm(values, company.settings, isOrgAdmin)
    updateCompany.mutate({
      id: company._id,
      payload: {
        // The server merges settings; send nulls for what the form cleared.
        settings: settingsReplacementPatch(company.settings, nextSettings),
      },
    }, {
      onSuccess: () => reset(formValuesFromSettings(nextSettings)),
    })
  }

  const values = formValuesFromSettings(company.settings)

  if (!canUpdate) {
    return (
      <div className="space-y-5">
        <ReadOnlyInfoSection
          icon={UserRound}
          title="Site contact"
          description="The person staff or admins should contact for this company."
        >
          <div className="grid gap-5 sm:grid-cols-3">
            <InfoItem label="Contact name" value={values.contactName} />
            <InfoItem label="Contact email" value={values.contactEmail} />
            <InfoItem label="Contact phone" value={values.contactPhone} />
          </div>
        </ReadOnlyInfoSection>

        <ReadOnlyInfoSection
          icon={MapPin}
          title="Location"
          description="Address and timezone used for site-specific schedules and reports."
        >
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <InfoItem label="Street" value={values.street} />
            <InfoItem label="City" value={values.city} />
            <InfoItem label="State" value={values.state} />
            <InfoItem label="Country" value={countryLabel(values.country)} />
            <InfoItem label="Postal code" value={values.postalCode} />
            <InfoItem label="Timezone" value={values.timezone} />
          </div>
        </ReadOnlyInfoSection>

        <ReadOnlyInfoSection
          icon={FileText}
          title="Instructions"
          description="Operational notes for site rules, check-in flow, and admin context."
        >
          <p className="whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm leading-6 text-gray-700 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-300">
            {displayValue(values.clientInstructions)}
          </p>
        </ReadOnlyInfoSection>

        {isOrgAdmin && (
          <ReadOnlyInfoSection
            icon={KeyRound}
            title="Company credentials"
            description="Sensitive site access details. Visible to organization admins only — never shown to staff or company managers."
          >
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              <InfoItem label="Console link" value={values.credentialConsoleLink} />
              <InfoItem label="Portal URL" value={values.credentialPortalUrl} />
              <InfoItem label="Company ID" value={values.credentialCompanyId} />
              <InfoItem label="Username" value={values.credentialUsername} />
              <SecretInfoItem label="Password" value={values.credentialPassword} />
              <InfoItem label="Phone username" value={values.credentialPhoneUsername} />
              <SecretInfoItem label="Phone password" value={values.credentialPhonePassword} />
              <InfoItem label="Phone extension" value={values.credentialPhoneExtensionNumber} />
              <SecretInfoItem label="Gate code" value={values.credentialGateCode} />
              <SecretInfoItem label="Alarm code" value={values.credentialAlarmCode} />
              <InfoItem label="Wifi network" value={values.credentialWifiName} />
              <SecretInfoItem label="Wifi password" value={values.credentialWifiPassword} />
            </div>
            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <InfoItem label="Dispense details" value={values.credentialDispenseDetails} />
              <InfoItem
                label="Dispense extensions"
                value={values.credentialDispenseExtensionNumbers}
              />
            </div>
            {values.credentialDispenseOperators?.length ? (
              <div className="mt-5 space-y-3">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                  Dispense operators
                </p>
                <div className="grid gap-3">
                  {values.credentialDispenseOperators.map((operator, index) => (
                    <div
                      key={`${operator.name}-${index}`}
                      className="grid gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/50 sm:grid-cols-2 lg:grid-cols-4"
                    >
                      <InfoItem label="Name" value={operator.name} />
                      <InfoItem label="Username" value={operator.username} />
                      <SecretInfoItem label="Password" value={operator.password} />
                      <InfoItem label="Extension" value={operator.extensionNumber} />
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            {values.credentialCustomFields?.length ? (
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {values.credentialCustomFields.map((field, index) => (
                  <InfoItem
                    key={`${field.label}-${index}`}
                    label={field.label || 'Custom field'}
                    value={field.value}
                  />
                ))}
              </div>
            ) : null}
            {values.credentialNotes?.trim() ? (
              <p className="mt-4 whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm leading-6 text-gray-700 dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-300">
                {values.credentialNotes}
              </p>
            ) : null}
          </ReadOnlyInfoSection>
        )}
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <fieldset
        disabled={!canUpdate || updateCompany.isPending}
        className="space-y-5 disabled:opacity-75"
      >
        <SettingsSection
          icon={UserRound}
          title="Site contact"
          description="The person staff or admins should contact for this client/site."
        >
          <div className="grid gap-4 md:grid-cols-3">
            <Input label="Contact name" {...register('contactName')} />
            <Input
              label="Contact email"
              {...register('contactEmail')}
              error={errors.contactEmail?.message}
            />
            <Input label="Contact phone" {...register('contactPhone')} />
          </div>
        </SettingsSection>

        <SettingsSection
          icon={MapPin}
          title="Location"
          description="Address and timezone used for site-specific schedules and reports."
        >
          <div className="grid gap-4 md:grid-cols-2">
            <Input label="Street" {...register('street')} />
            <Input label="City" {...register('city')} />
            <Input label="State" {...register('state')} />
            <Controller
              control={control}
              name="country"
              render={({ field }) => (
                <Select
                  label="Country"
                  options={NATIONALITY_OPTIONS}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  placeholder="Select country"
                  searchable
                  searchPlaceholder="Search countries..."
                />
              )}
            />
            <Input label="Postal code" {...register('postalCode')} />
            <Input label="Timezone" {...register('timezone')} placeholder="Asia/Karachi" />
          </div>
        </SettingsSection>

        <SettingsSection
          icon={FileText}
          title="Instructions"
          description="Operational notes for site rules, check-in flow, and admin context."
        >
          <div>
            <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
              Client instructions
            </label>
            <textarea
              {...register('clientInstructions')}
              className={textAreaClasses}
              placeholder="Site rules, parking, dress expectations, reporting steps..."
            />
          </div>
        </SettingsSection>

        {isOrgAdmin && (
          <SettingsSection
            icon={KeyRound}
            title="Company credentials"
            description="Sensitive site access details. Managed by organization admins only — never shown to staff or company managers."
          >
            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
              <ShieldCheck className="h-4 w-4 shrink-0" aria-hidden />
              Admin only — these values are hidden from everyone else in the organization.
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Console link"
                {...register('credentialConsoleLink')}
                error={errors.credentialConsoleLink?.message}
                placeholder="https://console.example.com"
              />
              <Input
                label="Portal URL"
                {...register('credentialPortalUrl')}
                error={errors.credentialPortalUrl?.message}
                placeholder="https://portal.example.com"
              />
              <Input
                label="Company ID"
                {...register('credentialCompanyId')}
                error={errors.credentialCompanyId?.message}
                placeholder="1234"
                maxLength={4}
              />
              <Input label="Username" {...register('credentialUsername')} autoComplete="off" />
              <Input
                label="Password"
                type="password"
                showPasswordToggle
                autoComplete="new-password"
                {...register('credentialPassword')}
              />
              <Input
                label="Phone username"
                {...register('credentialPhoneUsername')}
                autoComplete="off"
              />
              <Input
                label="Phone password"
                type="password"
                showPasswordToggle
                autoComplete="new-password"
                {...register('credentialPhonePassword')}
              />
              <Input
                label="Phone extension number"
                {...register('credentialPhoneExtensionNumber')}
                autoComplete="off"
              />
              <Input
                label="Gate code"
                type="password"
                showPasswordToggle
                autoComplete="off"
                {...register('credentialGateCode')}
              />
              <Input
                label="Alarm code"
                type="password"
                showPasswordToggle
                autoComplete="off"
                {...register('credentialAlarmCode')}
              />
              <Input label="Wifi network" {...register('credentialWifiName')} autoComplete="off" />
              <Input
                label="Wifi password"
                type="password"
                showPasswordToggle
                autoComplete="off"
                {...register('credentialWifiPassword')}
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <Input
                label="Dispense system details"
                {...register('credentialDispenseDetails')}
                placeholder="System URL, login notes, workflow..."
              />
              <Input
                label="Dispense extension numbers"
                {...register('credentialDispenseExtensionNumbers')}
                placeholder="1001, 1002, 1003"
              />
            </div>
            <div className="space-y-3 rounded-xl border border-gray-200 p-3 dark:border-gray-700">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                  Dispense operators
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    appendOperator({
                      name: '',
                      username: '',
                      password: '',
                      extensionNumber: '',
                    })
                  }
                >
                  <Plus className="h-4 w-4" aria-hidden />
                  <span className="ml-1">Add operator</span>
                </Button>
              </div>
              {operatorFields.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No operators added.
                </p>
              ) : (
                <div className="space-y-3">
                  {operatorFields.map((field, index) => (
                    <div
                      key={field.id}
                      className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/50"
                    >
                      <div className="mb-3 flex justify-end">
                        <button
                          type="button"
                          onClick={() => removeOperator(index)}
                          className="rounded-md p-1 text-gray-500 hover:bg-red-50 hover:text-red-600 dark:text-gray-400 dark:hover:bg-red-950/30 dark:hover:text-red-300"
                          aria-label={`Remove operator ${index + 1}`}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden />
                        </button>
                      </div>
                      <div className="grid gap-3 md:grid-cols-2">
                        <Input
                          label="Operator name"
                          {...register(`credentialDispenseOperators.${index}.name`)}
                        />
                        <Input
                          label="Username"
                          {...register(`credentialDispenseOperators.${index}.username`)}
                        />
                        <Input
                          label="Password"
                          type="password"
                          showPasswordToggle
                          autoComplete="new-password"
                          {...register(`credentialDispenseOperators.${index}.password`)}
                        />
                        <Input
                          label="Extension number"
                          {...register(`credentialDispenseOperators.${index}.extensionNumber`)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-3 rounded-xl border border-gray-200 p-3 dark:border-gray-700">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                  Custom fields
                </p>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => appendCustomField({ label: '', value: '', type: '' })}
                >
                  <Plus className="h-4 w-4" aria-hidden />
                  <span className="ml-1">Add field</span>
                </Button>
              </div>
              {customFieldRows.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No custom fields added.
                </p>
              ) : (
                <div className="space-y-3">
                  {customFieldRows.map((field, index) => (
                    <div
                      key={field.id}
                      className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/50"
                    >
                      <div className="mb-3 flex justify-end">
                        <button
                          type="button"
                          onClick={() => removeCustomField(index)}
                          className="rounded-md p-1 text-gray-500 hover:bg-red-50 hover:text-red-600 dark:text-gray-400 dark:hover:bg-red-950/30 dark:hover:text-red-300"
                          aria-label={`Remove custom field ${index + 1}`}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden />
                        </button>
                      </div>
                      <div className="grid gap-3 md:grid-cols-[1fr_1fr_140px]">
                        <Input
                          label="Label"
                          {...register(`credentialCustomFields.${index}.label`)}
                          error={errors.credentialCustomFields?.[index]?.label?.message}
                        />
                        <Input
                          label="Value"
                          {...register(`credentialCustomFields.${index}.value`)}
                        />
                        <Input
                          label="Type"
                          {...register(`credentialCustomFields.${index}.type`)}
                          placeholder="text"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
                Notes
              </label>
              <textarea
                {...register('credentialNotes')}
                className={textAreaClasses}
                placeholder="Access instructions, key box location, contacts for lockout..."
              />
            </div>
          </SettingsSection>
        )}
      </fieldset>

      <div className="sticky bottom-4 z-10 flex flex-col gap-3 rounded-xl border border-gray-200 bg-white/95 p-3 shadow-lg backdrop-blur dark:border-gray-700 dark:bg-gray-900/95 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Building2 className="h-4 w-4 text-primary" aria-hidden />
          Company info is saved for {company.name}.
        </div>
        <PermissionGate module="companies" action="update">
          <Button type="submit" disabled={!canUpdate || !isDirty} loading={updateCompany.isPending}>
            Save settings
          </Button>
        </PermissionGate>
      </div>
    </form>
  )
}

CompanySettingsTab.displayName = 'CompanySettingsTab'
